import { EventEmitter } from "events"
import dispatcher from "../dispatcher"
import moment from "moment-timezone"
import { IMarketingInfo, ITimezone, IUserDocument, IAccountDocument, ISimpleTime, IPayType, IPosition, IJobDocument, IJobPhase, IEquipmentDocument, EPlanType, IClientDocument } from "../models"
import { getTimezonesList } from "../actions/AccountActions"
import { lightBlue500 } from "material-ui/styles/colors"
import * as permissionHelper from "../helpers/permissionHelper"

class AccountStore extends EventEmitter {
	Users: Array<IUserDocument> = []
	retrievingAccount: boolean = false
	proxyAccountId: string
	CurrentAccount: IAccountDocument
	Timezones: ITimezone[] = []
	positionDefaultColor = lightBlue500
	ScheduleVirtualSimpleTime: ISimpleTime = {
		Year: 2000,
		Month: 0,
		Day: 1,
		Hour: 0,
		Minute: 0
	}
	InvalidPayTypeId = "InvalidPayTypeId"
	// if GlobalPunchAdd, we should show empty paytype
	EmptyPayTypeId = "EmptyPayTypeId"
	EmptyPayType = {
		Id: this.EmptyPayTypeId,
		Name: ""
	} as IPayType

	// if GlobalPunchEdit, we should show diff hierarchy and paytype etc 
	DiffHierarchyLevel = "DiffHierarchyLevel"
	DiffJobId = "DiffJobId"
	DiffPhaseId = "DiffPhaseId"
	DiffClientId = "DiffClientId"
	DiffPositionId = "DiffPositionId"
	DiffPayTypeId = "DiffPayTypeId"
	DiffPayClassId = "DiffPayClassId"
	DiffEquipmentId = "DiffEquipmentId"
	DiffJob = {
		Id: this.DiffJobId,
		Name: "Difference Job",
		Deleted: false,
		Active: true
	} as IJobDocument
	DiffPhase = {
		Id: this.DiffPhaseId,
		Name: "Difference Phase",
		Deleted: false,
		Active: true
	} as IJobPhase
	DiffClient = {
		Id: this.DiffClientId
	} as IClientDocument
	DiffPosition = {
		Id: this.DiffPositionId,
		Name: "Difference Position"
	} as IPosition
	DiffPayType = {
		Id: this.DiffPayTypeId,
		Name: "Difference PayType"
	} as IPayType
	DiffEquipment = {
		Id: this.DiffEquipmentId,
		Name: "Difference Equipment"
	} as IEquipmentDocument

	NonSelectItemValue = "NonSelect"

	accountsConfig: any

	jobTimezoneDic: Record<string, string> = {}

	constructor() {
		super()
		this.setMaxListeners(0)
	}

	async init(): Promise<void> {
		const timezones: ITimezone[] = await getTimezonesList()
		this.Timezones = timezones.sort((_tz1, _tz2) => (_tz1.Offset > _tz2.Offset ? 1 : -1))
		this.emit("timezonedata_change")
	}

	clear() {
		this.CurrentAccount = null
		this.proxyAccountId = null
	}

	getPositionDefaultColor() {
		return this.positionDefaultColor
	}

	getCurrentAccount() {
		return this.CurrentAccount
	}

	getCurrentAccountSettings() {
		return this.CurrentAccount ? this.CurrentAccount.AccountSettings : null
	}

	getUsers() {
		return this.Users
	}

	getTimezones() {
		return this.Timezones
	}

	setTimezones(_timezones: ITimezone[]) {
		this.Timezones = _timezones
		this.emit("timezonedata_change")
	}

	receivedUsers(users) {
		this.Users = users
		this.emit("change")
	}

	updateUser(user: any) {
		for (let i = 0; i < this.Users.length; i++) {
			if (this.Users[i].Id === user.Id) {
				this.Users[i] = user
				break
			}
		}
		this.emit("change")
	}

	clearCurrentAccount() {
		this.CurrentAccount = null
	}

	setCurrentAccount(account) {
		this.retrievingAccount = false
		if (account.PositionDefinitions) {
			account.PositionDefinitions = account.PositionDefinitions.sort(function (a, b) {
				const nameA = a.Name && a.Name.toUpperCase() // ignore upper and lowercase
				const nameB = a.Name && b.Name.toUpperCase() // ignore upper and lowercase
				if (nameA < nameB) {
					return -1
				}
				if (nameA > nameB) {
					return 1
				}
				// names must be equal
				return 0
			})
		}

		if (account.HierarchyItems) {
			account.HierarchyItems = account.HierarchyItems.sort(function (a, b) {
				const nameA = a.Label && a.Label.toUpperCase() // ignore upper and lowercase
				const nameB = b.Label && b.Label.toUpperCase() // ignore upper and lowercase
				if (nameA < nameB) {
					return -1
				}
				if (nameA > nameB) {
					return 1
				}
				// names must be equal
				return 0
			})
		}
		this.CurrentAccount = account
		this.emit("account_change")
	}

	getRetrievingAccount() {
		return this.retrievingAccount
	}

	setRetrievingAccount() {
		this.retrievingAccount = true
		this.emit("account_change")
	}

	getHierarchyEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableHierarchy ? true : false) : false
	}

	getI9Enabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableI9Workflow ? true : false) : false
	}

	getPayrollEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnablePayrollWorkflow ? true : false) : false
	}

	getPayrollLambdaEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnablePayrollLambda ? true : false) : false
	}

	getTimecardEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableTimecards ? true : false) : false
	}

	getEquipmentEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableEquipment ? true : false) : false
	}

	getAccrualsTimeoffEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableAccrualsTimeoff ? true : false) : false
	}

	getScheduleEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableSchedule ? true : false) : false
	}

	getWorkAvailabilityEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableWorkAvailability ? true : false) : false
	}

	getEmployeeApproveTimecardEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableEmployeeApproveTimecard ? true : false) : false
	}

	getExtSupApproveTimecardEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableExtSupApproveTimecard ? true : false) : false
	}

	getPriorPeriodAdjustmentsEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnablePriorPeriodAdjustments ? true : false) : false
	}

	getSupervisorDirectReportEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableSupervisorDirectReport ? true : false) : false
	}

	getClientsEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableClients ? true : false) : false
	}

	getJobsEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableJobs ? true : false) : false
	}

	getJobTimezoneEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableJobTimezone ? true : false) : false
	}

	getPhasesEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnablePhases ? true : false) : false
	}

	getAssignJobPunchEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableAssignJobPunch ? true : false) : false
	}

	getPunchSplitEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnablePunchSplit ? true : false) : false
	}

	getAccountAgreementEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.RequireAgreement ? true : false) : false
	}

	getPrePunchScreeningEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnablePreQuestions ? true : false) : false
	}

	getPunchQuestionEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnablePunchQuestions ? true : false) : false
	}

	getHREnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableHR ? true : false) : false
	}

	getNoteAttributesEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableNoteAttributes ? true : false) : false
	}

	getCRMEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableCRM ? true : false) : false
	}

	getExpensesEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableExpenses ? true : false) : false
	}

	getPayClassEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnablePayClass ? true : false) : false

	}

	getUseBasePayRateInPayClassEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableUseBasePayRateInPayClass ? true : false) : false
	}


	getBlendedOvertimeRateEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableBlendedRateOvertime ? true : false) : false
	}

	getCalculationDayGroupEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableCalculationDayGroup ? true : false) : false
	}

	getAccrualManualAdjustmentEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableManualAccrualAdjustment ? true : false) : false
	}

	getOneHourTimeoffIncrement() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.OneHourTimeoffIncrement ? true : false) : false
	}

	getPunchRuleEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnablePunchRule ? true : false) : false
	}

	getPunchRuleStageEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnablePunchRuleStage ? true : false) : false
	}

	getForceEmployeeAccountPosition() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.ForceEmployeeAccountPosition ? true : false) : false
	}

	getHideBilling() {
		const account = this.getCurrentAccount()
		return account ? (!permissionHelper.isGlobalAdmin() && account.AccountSettings.FeatureSettings.HideBilling ? true : false) : true
	}

	getCustomRoundingRuleEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableCustomRoundingRule ? true : false) : false
	}

	getRateTotalsEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableRateTotals ? true : false) : false
	}

	getTimecardTimeExpensesEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableTimecardExpenses ? true : false) : false
	}

	getTimecardAmountExpensesEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableTimecardAmountExpenses ? true : false) : false
	}

	getExpenseIsPayrollTime() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.ExpenseIsPayrollTime ? true : false) : false
	}

	getShowPayrollRates() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.ShowPayrollRates ? true : false) : false
	}

	getExplicitEmployeeHierarchyAssignment() {
		const account = this.getCurrentAccount()
		return account && account.AccountSettings.HierarchySettings && account.AccountSettings.HierarchySettings.DisableHierarchyItemsForEmployeeUnlessAssigned ?
			account.AccountSettings.HierarchySettings.DisableHierarchyItemsForEmployeeUnlessAssigned : false
	}

	getExplicitAccountUserHierarchyAssignment() {
		const account = this.getCurrentAccount()
		return account && account.AccountSettings.HierarchySettings && account.AccountSettings.HierarchySettings.DisableHierarchyItemsForAccountUserUnlessAssigned ?
			account.AccountSettings.HierarchySettings.DisableHierarchyItemsForAccountUserUnlessAssigned : false
	}

	getEnableWorkflows() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableWorkflows ? true : false) : false
	}

	getAllowSTRIPEPaymentMethod() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.AllowSTRIPEPaymentMethod ? true : false) : false
	}

	getHidePaywall() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.HidePaywall ? true : false) : false
	}

	getHideOT2() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.HideOT2 ? true : false) : false
	}

	getEnableOTPolicy() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableOTPolicy ? true : false) : false
	}

	getEnableDailyScheduleBudget() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.DailyScheduleBudget ? true : false) : false
	}

	getEnableResourcePlannerSchedule() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.ResourcePlannerSchedule ? true : false) : false
	}

	getEnableWorkInstructions() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.WorkInstructions ? true : false) : false
	}

	getAllowMobileLocationFraudPreventionOnDevice() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.AllowMobileLocationFraudPreventionOnDevice ? true : false) : false
	}

	getEnableBreadcrumb() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableBreadcrumb ? true : false) : false
	}

	getEnableCAMealBreakNotification() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableCAMealBreakNotification ? true : false) : false
	}

	getEnablePayGroupAssignEmployeeLevel() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnablePayGroupAssignEmployeeLevel ? true : false) : false
	}

	getShowPayClassOnESS() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.ShowPayClassOnESS ? true : false) : false
	}

	getEnableEmployeeLock() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableEmpLock ? true : false) : false
	}

	getEnableSupervisorLock() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableSupLock ? true : false) : false
	}

	getEnableATS() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableATS ? true : false) : false
	}

	getEnableMessageCenter() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableMessageCenter ? true : false) : false
	}
	
	getMomentFromDate(_date) {
		return moment(_date)
	}

	getHierarchyItem(_hierarchyItemId) {
		return this.getCurrentAccount().HierarchyItems.find((_item) => _item.Id === _hierarchyItemId)
	}

	getMarketingInfoFromLocalStorage(): IMarketingInfo {
		return {
			UtmSource: this.getLocalStorageValue(`findd_marketing_utm_source`),
			UtmMedium: this.getLocalStorageValue(`findd_marketing_utm_medium`),
			UtmCampaign: this.getLocalStorageValue(`findd_marketing_utm_campaign`),
			UtmTerm: this.getLocalStorageValue(`findd_marketing_utm_term`),
			UtmContent: this.getLocalStorageValue(`findd_marketing_utm_content`),
			AdId: this.getLocalStorageValue(`findd_marketing_ad_id`),
			AdsetId: this.getLocalStorageValue(`findd_marketing_adset_id`),
			CampaignId: this.getLocalStorageValue(`findd_marketing_campaign_id`),
			AdName: this.getLocalStorageValue(`findd_marketing_ad_name`),
			AdsetName: this.getLocalStorageValue(`findd_marketing_adset_name`),
			CampaignName: this.getLocalStorageValue(`findd_marketing_campaign_name`),
			Placement: this.getLocalStorageValue(`findd_marketing_placement`),
			SiteSourceName: this.getLocalStorageValue(`findd_marketing_site_source_name`),
			LandingPage: this.getLocalStorageValue(`findd_marketing_landing_page`),
		}
	}

	getLocalStorageValue(id: string) {
		const localStorageVal = localStorage.getItem(id)

		if (localStorageVal === "null" || localStorageVal === "") {
			return null
		} else {
			return localStorageVal
		}
	}

	setMarketingDetails(urlSearchParams) {
		const existingMarketingInfo = this.getMarketingInfoFromLocalStorage()

		if (!existingMarketingInfo.UtmSource || urlSearchParams.get('utm_source')) {
			localStorage.setItem('findd_marketing_utm_source', urlSearchParams.get('utm_source'))
		}
		if (!existingMarketingInfo.UtmMedium || urlSearchParams.get('utm_medium')) {
			localStorage.setItem('findd_marketing_utm_medium', urlSearchParams.get('utm_medium'))
		}
		if (!existingMarketingInfo.UtmCampaign || urlSearchParams.get('utm_campaign')) {
			localStorage.setItem('findd_marketing_utm_campaign', urlSearchParams.get('utm_campaign'))
		}
		if (!existingMarketingInfo.UtmTerm || urlSearchParams.get('utm_term')) {
			localStorage.setItem('findd_marketing_utm_term', urlSearchParams.get('utm_term'))
		}
		if (!existingMarketingInfo.UtmContent || urlSearchParams.get('utm_content')) {
			localStorage.setItem('findd_marketing_utm_content', urlSearchParams.get('utm_content'))
		}
		if (!existingMarketingInfo.AdId || urlSearchParams.get('ad_id')) {
			localStorage.setItem('findd_marketing_ad_id', urlSearchParams.get('ad_id'))
		}
		if (!existingMarketingInfo.AdsetId || urlSearchParams.get('adset_id')) {
			localStorage.setItem('findd_marketing_adset_id', urlSearchParams.get('adset_id'))
		}
		if (!existingMarketingInfo.CampaignId || urlSearchParams.get('campaign_id')) {
			localStorage.setItem('findd_marketing_campaign_id', urlSearchParams.get('campaign_id'))
		}
		if (!existingMarketingInfo.AdName || urlSearchParams.get('ad_name')) {
			localStorage.setItem('findd_marketing_ad_name', urlSearchParams.get('ad_name'))
		}
		if (!existingMarketingInfo.AdsetName || urlSearchParams.get('adset_name')) {
			localStorage.setItem('findd_marketing_adset_name', urlSearchParams.get('adset_name'))
		}
		if (!existingMarketingInfo.CampaignName || urlSearchParams.get('campaign_name')) {
			localStorage.setItem('findd_marketing_campaign_name', urlSearchParams.get('campaign_name'))
		}
		if (!existingMarketingInfo.Placement || urlSearchParams.get('placement')) {
			localStorage.setItem('findd_marketing_placement', urlSearchParams.get('placement'))
		}
		if (!existingMarketingInfo.SiteSourceName || urlSearchParams.get('site_source_name')) {
			localStorage.setItem('findd_marketing_site_source_name', urlSearchParams.get('site_source_name'))
		}
		if (!existingMarketingInfo.LandingPage || urlSearchParams.get('landing_page')) {
			localStorage.setItem('findd_marketing_landing_page', urlSearchParams.get('landing_page'))
		}
	}

	setProxyAccountId(proxyId) {
		const localStorageProxy = localStorage.getItem("findd_proxy")
		if (proxyId != null && !sessionStorage.getItem("findd_account_manager")) {
			this.proxyAccountId = proxyId
			localStorage.setItem("findd_proxy", proxyId)
		}

		if (proxyId === null && localStorageProxy !== null) {
			this.proxyAccountId = localStorageProxy
		}
	}

	getProxyAccountId() {
		return this.proxyAccountId
	}

	isFreeAccount() {
		const account = this.getCurrentAccount()
		return account.BillingInfo.PlanType === EPlanType.Free
	}

	setAccountsConfig(config) {
		this.accountsConfig = config
		this.emit("accounts_config")
	}

	getAccountsConfig() {
		return this.accountsConfig
	}

	getHierarchyDefinitionLevelCount() {
		return this.CurrentAccount.HierarchyDefinitions.length
	}

	handleActions(action) {
		switch (action.type) {
			case "LOGOUT": {
				this.clearCurrentAccount()
				break
			}
			case "UPDATE_ACCOUNT": {
				this.setCurrentAccount(action.account)
				break
			}
			case "UPDATE_ACCOUNT_USERS": {
				this.receivedUsers(action.accountUsers)
				break
			}
			case "UPDATE_ACCOUNT_USER": {
				this.updateUser(action.accountUser)
				break
			}
			case "RETRIEVING_ACCOUNT": {
				this.setRetrievingAccount()
				break
			}
			case "ACCOUNTS_CONFIG": {
				this.setAccountsConfig(action.config)
				break
			}
		}
	}

	getDisableEmployeeApprovalException() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.DisableEmployeeApprovalException ? true : false) : false
	}

	getDisableSupervisorApprovalException() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.DisableSupervisorApprovalException ? true : false) : false
	}

	getDisableLocationExceptionWhenNoGPS() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.DisableLocationExceptionWhenNoGPS ? true : false) : false
	}
	getMedicalAccount() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.MedicalAccount ? true : false) : false
	}

	getTrendsEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableTrends ? true : false) : false
	}

	getPayRateTimeOffEditAllowed() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.AllowTimeOffPayRateEdit ? true : false) : false
	}

	getJobTimezoneId(jobId) {
		return this.jobTimezoneDic[jobId]
	}

	setJobTimezoneId(jobId: string, timezoneId: string) {
		if (!this.jobTimezoneDic.hasOwnProperty(jobId)) {
			this.jobTimezoneDic[jobId] = timezoneId
		}
	}

	getIVREnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableIVR ? true : false) : false
	}

	getGeoFenceTrackingEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableGeoFenceTracking ? true : false) : false
	}

	getLivenessDetectionEnabled() {
		const account = this.getCurrentAccount()
		return account ? (account.AccountSettings.FeatureSettings.EnableLiveness ? true : false) : false
	}
}

const accountStore = new AccountStore()
dispatcher.register(accountStore.handleActions.bind(accountStore))

export default accountStore